const Blockly = require('blockly');
const logging = require('@sstdev/lib_logging');
const _ = require('lodash');
const {
    gatherAllListsIntoChildren,
    blockName,
    addSecurityModifiersToCode,
    addSecurityModifiersToBlock,
    prettyName
} = require('../../helpers/blockInitMethods');

module.exports = class BaseMdcBlockGroup {
    constructor(hNodeTypeGroup, categorySpec, color, subset = '') {
        this.hNodeTypeGroup = hNodeTypeGroup;
        this.config = categorySpec;
        this.buildControls = Array.isArray(this.config.controls)
            ? this.config.controls
            : this.config.controls?.[subset];
        this.color = color;
        this.includeSecurityModifiers = this.config.includeSecurityModifiers || false;
        this.blockDescription = this.config.groupDescription || '';
        this.hasChildren = this.config.hasChildren || false;
        this.allowedChildrenTypes = this.config.allowedChildrenTypes || [];
        this.blockTypesAllowedToFollowThisOne =
            // if it is explicitly set to null, then allow all types to follow
            this.config.blockTypesAllowedToFollowThisOne === null
                ? null
                : // if it is just absent, default to allow nothing.
                  this.config.blockTypesAllowedToFollowThisOne || [];
    }

    addCodeGenerator() {
        let mdcBlockGroup = this;
        this.buildControls.forEach(function (hNodeType) {
            Blockly.JavaScript[blockName(hNodeType, mdcBlockGroup.hNodeTypeGroup)] = function (blocklyBlock) {
                const code = {
                    id: toHtmlCompatibleId(blocklyBlock.id),
                    viewport: ['all'],
                    hNodeType: hNodeType,
                    hNodeTypeGroup: mdcBlockGroup.hNodeTypeGroup,
                    blockId: blocklyBlock.id
                };

                const textTitle = blocklyBlock.getFieldValue('title');
                if (textTitle || textTitle === '') {
                    code.title = textTitle;
                }

                gatherAllListsIntoChildren(code, blocklyBlock);
                if (mdcBlockGroup.includeSecurityModifiers) {
                    addSecurityModifiersToCode(code, blocklyBlock);
                }

                return mdcBlockGroup.addCodeGeneratorCustom(code, blocklyBlock, hNodeType);
            };
        });
    }

    addToBlocks() {
        const mdcBlock = this;
        this.buildControls.forEach(function (hNodeType) {
            // logging.debug(`[BLOCKLY] registering ${blockName(hNodeType, mdcBlock.hNodeTypeGroup)} block`);
            Blockly.Blocks[blockName(hNodeType, mdcBlock.hNodeTypeGroup)] = {
                init: function () {
                    const blocklyBlock = this;
                    Blockly.Extensions.apply('collapse_on_double_click', blocklyBlock, false);

                    this.setInputsInline(true);

                    const topInput = blocklyBlock
                        .appendDummyInput('topInput')
                        .setAlign(Blockly.ALIGN_LEFT)
                        .appendField(mdcBlock.getBlockName(hNodeType));

                    if (!mdcBlock.config.suppressTitle) {
                        topInput
                            .appendField('  Title:', 'titleLabel')
                            .appendField(new Blockly.FieldTextInput(''), 'title');
                    }

                    blocklyBlock.setPreviousStatement(true, mdcBlock.hNodeTypeGroup);
                    blocklyBlock.setNextStatement(true, mdcBlock.blockTypesAllowedToFollowThisOne);

                    blocklyBlock.setColour(mdcBlock.color[0]);
                    blocklyBlock.setTooltip(mdcBlock.blockDescription);
                    // I'm not sure why the 'D' here needs to be capitalized, but
                    // it does or NGINX won't find it.
                    blocklyBlock.setHelpUrl(`./Docs/${mdcBlock.hNodeTypeGroup}s/${hNodeType.toLowerCase()}.html`);

                    mdcBlock.addToBlocksCustom(mdcBlock.color, blocklyBlock, hNodeType, mdcBlock, topInput);

                    if (mdcBlock.includeSecurityModifiers) {
                        addSecurityModifiersToBlock(blocklyBlock);
                    }
                }
            };
        });
    }
    /**
     * @typedef {Object} Toolbox
     * @property {string} kind - The kind of toolbox object.
     * @property {ToolboxCategory[]} contents - The contents of the toolbox object.
     * @typedef {Object} ToolboxCategory
     * @property {string} kind - The kind of toolbox object.
     * @property {string} name - The name of the toolbox object.
     * @property {string} colour - The colour of the toolbox object.
     * @property {Array} contents - The contents of the toolbox object.
     * @typedef {ToolboxBlock[]} ToolboxBlock
     * @property {string} kind - The kind of toolbox object.
     * @property {string} type - The type of the toolbox object.
     */
    /**
     * Adds a toolbox category to the provided toolbox.
     *
     * @param {Toolbox} toolbox - The toolbox object to add the category to.
     * @returns {void}
     */
    addToolboxCategory(toolbox) {
        let that = this;
        let section = {
            kind: 'category',
            name: this.getPrettyBlockGroupName(),
            colour: this.color[1], //take the hexRgb (color = [hue, hexRgb])
            contents: []
        };
        this.buildControls.forEach(function (name) {
            section.contents.push({
                kind: 'block',
                type: blockName(name, that.hNodeTypeGroup)
            });
        });
        this.customizeToolbox(section);
        toolbox.contents.push(section);
    }

    getBlockName(hNodeType) {
        return prettyName(hNodeType);
    }
    // DISABLED ESLINT BECAUSE these functions are here as templates for extenders

    /**
     * @param {{[key: string]: any}} code
     * @param {Blockly.Block} block
     * @param {string} hNodeType
     * @returns string
     */
    // eslint-disable-next-line no-unused-vars
    addCodeGeneratorCustom(code, block, hNodeType) {
        return JSON.stringify(code);
    }

    // eslint-disable-next-line no-unused-vars
    addToBlocksCustom(color, block, hNodeType) {
        // Do nothing unless overridden.
    }

    /**
     *
     * @param {Object} section JSON representation of Toolbox Category
     * @description for more information see
     * https://developers.google.com/blockly/guides/configure/web/toolbox#json
     */
    // eslint-disable-next-line no-unused-vars
    customizeToolbox(section) {
        // Do nothing unless overridden.
    }

    getPrettyBlockGroupName() {
        return _.startCase(this.hNodeTypeGroup) + 's';
    }

    blockName(hNodeType) {
        return blockName(hNodeType, this.hNodeTypeGroup);
    }
};
function toHtmlCompatibleId(value) {
    let temp = value;
    try {
        temp = Buffer.from(value).toString('base64');
    } catch (err) {
        logging.error(`There was a problem converting id ${value} to base64.`);
        logging.error(err);
    }
    temp = temp.replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
    //always assure a letter as the first character.
    return 'bb' + temp;
}
